import {
  AccountStatusEnum,
  BillGroupErrorStatusEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { toDateShort } from '@monetize/utils/core';
import { Popover } from '@chakra-ui/react';
import {
  MAlert,
  MButton,
  MFlex,
  MText,
  MTooltip,
} from '../../../../components/Monetize';
import { usePastInvoice } from '../hooks/usePastInvoice';

export interface BillGroupGenerateInvoicePopoverProps {
  billGroupId: string;
  nextInvoiceDate: string;
  billGroupStatus: string;
  accountId: string;
  accountStatus: AccountStatusEnum;
  invoiceDelayDirty?: Maybe<boolean>;
  invoiceDelay?: Maybe<number>;
  errorStatus?: BillGroupErrorStatusEnum;
}
export const BillGroupGenerateInvoicePopover = ({
  billGroupId,
  nextInvoiceDate,
  billGroupStatus,
  accountId,
  accountStatus,
  invoiceDelay,
  invoiceDelayDirty,
  errorStatus,
}: BillGroupGenerateInvoicePopoverProps) => {
  const isAccountActive = accountStatus === AccountStatusEnum.ACTIVE;
  const { canUpdate, hasPastInvoices, isLoading, generatePreviousInvoice } =
    usePastInvoice({
      accountId,
      billGroupId,
      billGroupStatus,
      nextInvoiceDate,
      invoiceDelay,
      invoiceDelayDirty,
    });

  if (!canUpdate || !hasPastInvoices) {
    return null;
  }

  if (errorStatus === BillGroupErrorStatusEnum.TAX_CALCULATION_ERROR) {
    return (
      <MAlert type="error" mt="2">
        <MText>
          There is a tax calculation error on your most recent invoice.
        </MText>
      </MAlert>
    );
  }

  return (
    <Popover.Root
      lazyMount
      unmountOnExit
      positioning={{ placement: 'bottom-end' }}
    >
      <Popover.Trigger asChild>
        <MFlex cursor="pointer" gap="1" ml="auto" h="8" alignItems="center">
          <MText textAlign="right">Pending Invoice</MText>
          <MFlex
            align="center"
            justify="center"
            w="5"
            h="5"
            borderRadius="50%"
            bg="tIndigo.base"
            color="tWhite.base"
            fontSize="xs"
            fontWeight="700"
          >
            1
          </MFlex>
        </MFlex>
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content>
          <Popover.Arrow>
            <Popover.ArrowTip />
          </Popover.Arrow>
          <Popover.Header>Pending Invoice</Popover.Header>
          <Popover.Body>
            <MText>
              This Bill Group has a pending invoice. Next Invoice Date is{' '}
              {toDateShort(nextInvoiceDate)}.
            </MText>
          </Popover.Body>
          <Popover.Footer display="flex" justifyContent="flex-end">
            <MTooltip
              label="Invoice generation is not available because the account is not Active. Update the account status to Active to enable invoice generation."
              isDisabled={isAccountActive}
              shouldWrapChildren
            >
              <MButton
                size="sm"
                isLoading={isLoading}
                disabled={isLoading || !isAccountActive}
                onClick={() =>
                  generatePreviousInvoice({ accountId, billGroupId })
                }
              >
                Generate
              </MButton>
            </MTooltip>
          </Popover.Footer>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
};
